import { isNotNullOrUndefined, isNullOrUndefined, isStringEmpty, isStringNotEmpty } from './objectUtils';

export const RESUME_TOKEN_PARAM = 'rt';
// per-tab storage, never sent to a server, so a refresh mid-redemption does not lose the link
const PENDING_TOKEN_STORAGE_KEY = 'readyrx.pendingResumeToken';
// a captured token is only pending while a redemption could still be under way; one left behind by
// an interrupted load must not hold up a page opened in the same tab later on
export const PENDING_TOKEN_MAX_AGE_MS = 5 * 60 * 1000;

let pending = null;

const isFresh = (entry, now) => isStringNotEmpty(entry?.token) && Number.isFinite(entry?.capturedAt) && (now - entry.capturedAt) <= PENDING_TOKEN_MAX_AGE_MS;

const readStorage = (win) => {
	let serialized = null;
	try {
		serialized = win?.sessionStorage?.getItem(PENDING_TOKEN_STORAGE_KEY) ?? null;
	} catch {
		return null;
	}
	if (isStringEmpty(serialized)) {
		return null;
	}

	try {
		return JSON.parse(serialized);
	} catch {
		// an entry this build cannot read is stale by definition and is cleared like an old one
		return {};
	}
};

const writeStorage = (win, entry) => {
	try {
		if (isNullOrUndefined(entry)) {
			win?.sessionStorage?.removeItem(PENDING_TOKEN_STORAGE_KEY);
		} else {
			win?.sessionStorage?.setItem(PENDING_TOKEN_STORAGE_KEY, JSON.stringify(entry));
		}
	} catch {
		// private mode or a blocked store: memory alone still carries the token for this load
	}
};

const stripParam = (serialized) => {
	const params = new URLSearchParams(serialized);
	const token = params.get(RESUME_TOKEN_PARAM);
	params.delete(RESUME_TOKEN_PARAM);
	return { token: isStringNotEmpty(token) ? token : null, rest: params.toString() };
};

/**
 * Takes the resume token off the address bar and holds it in memory and per-tab storage. The token is
 * a sign-in credential, so it has to leave the url before tracking, session replay or the history log
 * read it. It is accepted from the query string or from the fragment, which never reaches a server.
 *
 * @param {Window} [win=window] - Window whose location is read and rewritten.
 * @returns {string|null} The captured token, or null when the url carried none.
 */
export const captureResumeTokenFromUrl = (win = window) => {
	const query = stripParam(win.location.search);
	const fragment = stripParam(win.location.hash.replace(/^#/, ''));
	const token = query.token ?? fragment.token;
	if (isStringEmpty(token)) {
		return null;
	}

	pending = { token, capturedAt: Date.now() };
	writeStorage(win, pending);
	const search = isStringNotEmpty(query.rest) ? `?${query.rest}` : '';
	// a fragment the token did not ride in is left byte-for-byte as it was
	const hash = isStringNotEmpty(fragment.token) ? (isStringNotEmpty(fragment.rest) ? `#${fragment.rest}` : '') : win.location.hash;
	win.history.replaceState(win.history.state, '', `${win.location.pathname}${search}${hash}`);

	return token;
};

/**
 * @param {Window} [win=window] - Window whose per-tab storage is read when memory holds nothing.
 * @returns {string|null} The token captured within the last few minutes, at boot or on a previous
 * load of this tab, and not yet redeemed. An older one is forgotten.
 */
export const getPendingResumeToken = (win = window) => {
	const now = Date.now();
	if (isFresh(pending, now)) {
		return pending.token;
	}
	pending = null;

	const stored = readStorage(win);
	if (isFresh(stored, now)) {
		return stored.token;
	}
	if (isNotNullOrUndefined(stored)) {
		writeStorage(win, null);
	}

	return null;
};

export const clearPendingResumeToken = (win = window) => {
	pending = null;
	writeStorage(win, null);
};
